import { useState, useRef, useEffect, Fragment } from "react";
import api, { getErrorMessage } from "@/lib/api";

// LUMOS brand palette.
const NEAR_BLACK = "#0B0B0C";
const PANEL = "#141416";
const OFF_WHITE = "#F0EDE8";
const RED = "#D14233";
const MUTED = "rgba(240,237,232,0.55)";

const PLAYFAIR = "'Playfair Display', Georgia, serif";
const DM = "'DM Sans', -apple-system, sans-serif";

const EXAMPLES = [
  "What are my current holdings?",
  "Show me cashflows for my largest fund",
  "What's my latest capital call?",
];

// 8-point starburst with a red center dot — inline SVG, no icon dependency.
function Starburst({ size = 30 }) {
  const c = size / 2;
  const rays = [];
  for (let i = 0; i < 8; i++) {
    const a = (Math.PI / 4) * i;
    const inner = size * 0.15;
    const outer = size * 0.46;
    rays.push(
      <line
        key={i}
        x1={c + Math.cos(a) * inner}
        y1={c + Math.sin(a) * inner}
        x2={c + Math.cos(a) * outer}
        y2={c + Math.sin(a) * outer}
        stroke={OFF_WHITE}
        strokeWidth={1.2}
        strokeLinecap="round"
      />
    );
  }
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
      {rays}
      <circle cx={c} cy={c} r={size * 0.1} fill={RED} />
    </svg>
  );
}

function Wordmark() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
      <Starburst size={26} />
      <div>
        <div
          style={{
            fontFamily: PLAYFAIR,
            fontWeight: 700,
            fontSize: "1.25rem",
            letterSpacing: "0.05em",
            color: OFF_WHITE,
            lineHeight: 1,
          }}
        >
          LUMOS<span style={{ color: RED }}>.</span>
        </div>
        <div style={{ fontFamily: DM, fontSize: "0.52rem", letterSpacing: "0.22em", color: MUTED, marginTop: 4 }}>
          AN ELEMENTAR PRODUCT
        </div>
      </div>
    </div>
  );
}

// Linkify ONLY Elementar Academy URLs in assistant text. Deliberately narrow: search_docs
// snippets can carry arbitrary client-document text, so auto-linkifying every URL would make an
// injected link clickable. The char class [\w\-/#] self-terminates before punctuation, so a
// trailing "." or ")" stays plain text and never breaks the href. No markdown, no raw HTML.
const ACADEMY_URL_RE = /(https:\/\/portal\.elementarai\.com\/academy[\w\-/#]*)/g;

function linkifyAcademy(text) {
  if (typeof text !== "string" || !text.includes("portal.elementarai.com/academy")) return text;
  return text.split(ACADEMY_URL_RE).map((part, i) =>
    i % 2 === 1 ? (
      <a
        key={i}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: RED, textDecoration: "underline", overflowWrap: "anywhere" }}
      >
        {part}
      </a>
    ) : (
      part
    )
  );
}

function Bubble({ role, children }) {
  const isUser = role === "user";
  return (
    <div style={{ display: "flex", justifyContent: isUser ? "flex-end" : "flex-start" }}>
      <div
        style={{
          maxWidth: "82%",
          padding: "9px 13px",
          borderRadius: 12,
          fontFamily: DM,
          fontSize: "0.86rem",
          lineHeight: 1.5,
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          wordBreak: "break-word",
          color: OFF_WHITE,
          background: isUser ? "rgba(209,66,51,0.18)" : PANEL,
          border: isUser ? "1px solid rgba(209,66,51,0.4)" : "1px solid rgba(240,237,232,0.08)",
        }}
      >
        {linkifyAcademy(children)}
      </div>
    </div>
  );
}

export default function LumosWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // {role: 'user'|'assistant', content}
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // PDF preview modal: { blob_path, document } or null when closed.
  const [preview, setPreview] = useState(null);
  const endRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    // optional call: scrollIntoView is absent in jsdom (tests)
    endRef.current?.scrollIntoView?.({ behavior: "smooth" });
  }, [messages, loading, open]);

  // Collapse the popup when the user clicks outside it.
  useEffect(() => {
    if (!open) return undefined;
    function onDown(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  function newChat() {
    setMessages([]);
    setInput("");
    setError("");
  }

  async function send(text) {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setError("");
    // Client-held transcript is sent as context; the backend re-derives tenant_id from the JWT every
    // turn and never trusts message content, so this is untrusted context, not an auth surface.
    // Strip UI-only fields (sources) so the backend _HistoryTurn schema receives only {role, content}.
    const history = messages.slice(-20).map((m) => ({ role: m.role, content: m.content }));
    setMessages((m) => [...m, { role: "user", content: q }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/lumos/chat", { question: q, history });
      setMessages((m) => [...m, { role: "assistant", content: data.answer, sources: data.sources || [] }]);
    } catch (e) {
      setError(getErrorMessage(e, "Lumos is unavailable right now."));
    } finally {
      setLoading(false);
    }
  }

  function onKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }

  // Closed: just the floating launcher (the starburst mark).
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        aria-label="Open Lumos"
        data-tour="lumos"
        style={{
          position: "fixed",
          bottom: 24,
          right: 24,
          zIndex: 9999,
          width: 56,
          height: 56,
          borderRadius: "50%",
          background: NEAR_BLACK,
          border: "1px solid rgba(240,237,232,0.18)",
          boxShadow: "0 6px 24px rgba(0,0,0,0.4)",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Starburst size={28} />
      </button>
    );
  }

  // Open: the side popup chat panel.
  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Lumos"
      style={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: 9999,
        width: 380,
        maxWidth: "92vw",
        height: 560,
        maxHeight: "80vh",
        display: "flex",
        flexDirection: "column",
        background: NEAR_BLACK,
        borderRadius: 16,
        border: "1px solid rgba(240,237,232,0.1)",
        boxShadow: "0 12px 48px rgba(0,0,0,0.5)",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "14px 16px",
          borderBottom: "1px solid rgba(240,237,232,0.08)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Wordmark />
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            onClick={newChat}
            aria-label="New chat"
            title="New chat"
            style={{
              background: "transparent", border: "1px solid rgba(240,237,232,0.18)", borderRadius: 8,
              color: MUTED, fontFamily: DM, fontSize: "0.72rem", cursor: "pointer", padding: "4px 9px",
            }}
          >
            New chat
          </button>
          <button
            onClick={() => setOpen(false)}
            aria-label="Close Lumos"
            style={{
              background: "transparent", border: "none", color: MUTED, fontSize: "1.4rem",
              lineHeight: 1, cursor: "pointer", padding: "0 4px",
            }}
          >
            ×
          </button>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
        {messages.length === 0 && (
          <div style={{ margin: "auto", textAlign: "center", maxWidth: 300 }}>
            <div style={{ fontFamily: PLAYFAIR, color: OFF_WHITE, fontSize: "1.05rem", marginBottom: 8 }}>
              Ask about your portfolio
            </div>
            <div style={{ fontFamily: DM, color: MUTED, fontSize: "0.8rem", marginBottom: 18 }}>
              Holdings, cashflows, NAV, and your documents — your accounts only.
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7, justifyContent: "center" }}>
              {EXAMPLES.map((ex) => (
                <button
                  key={ex}
                  onClick={() => send(ex)}
                  style={{
                    fontFamily: DM,
                    fontSize: "0.76rem",
                    color: OFF_WHITE,
                    background: PANEL,
                    border: "1px solid rgba(240,237,232,0.12)",
                    borderRadius: 999,
                    padding: "6px 12px",
                    cursor: "pointer",
                  }}
                >
                  {ex}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <Fragment key={i}>
            <Bubble role={m.role}>{m.content}</Bubble>
            {m.role === "assistant" && m.sources && m.sources.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "flex-start", maxWidth: "100%", minWidth: 0 }}>
                {m.sources.slice(0, 4).map((s, j) => (
                  <button
                    key={j}
                    onClick={() => s.blob_path && setPreview({ blob_path: s.blob_path, document: s.document })}
                    disabled={!s.blob_path}
                    title={`${s.document || "document"}${s.type ? " · " + s.type : ""}`}
                    style={{
                      fontFamily: DM, fontSize: "0.72rem", color: s.blob_path ? OFF_WHITE : MUTED,
                      background: PANEL, border: "1px solid rgba(240,237,232,0.12)", borderRadius: 8,
                      padding: "4px 9px", cursor: s.blob_path ? "pointer" : "default",
                      display: "flex", alignItems: "center", gap: 5,
                      maxWidth: "100%", minWidth: 0, overflow: "hidden",
                    }}
                  >
                    <span style={{ color: RED, flexShrink: 0 }}>⊞</span>
                    <span style={{
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "200px",
                    }}>
                      {s.document || "document"}
                    </span>
                  </button>
                ))}
                {m.sources.length > 4 && (
                  <span style={{ fontFamily: DM, fontSize: "0.72rem", color: MUTED, alignSelf: "center" }}>
                    +{m.sources.length - 4} more
                  </span>
                )}
              </div>
            )}
          </Fragment>
        ))}
        {loading && (
          <Bubble role="assistant">
            <span style={{ color: MUTED }}>Lumos is thinking…</span>
          </Bubble>
        )}
        <div ref={endRef} />
      </div>

      {error && (
        <div style={{ padding: "6px 16px", color: RED, fontFamily: DM, fontSize: "0.78rem" }}>{error}</div>
      )}

      <div style={{ padding: 12, borderTop: "1px solid rgba(240,237,232,0.08)", display: "flex", gap: 8 }}>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Ask Lumos…"
          aria-label="Ask Lumos"
          disabled={loading}
          style={{
            flex: 1,
            background: PANEL,
            border: "1px solid rgba(240,237,232,0.14)",
            borderRadius: 10,
            padding: "10px 13px",
            color: OFF_WHITE,
            fontFamily: DM,
            fontSize: "0.86rem",
            outline: "none",
          }}
        />
        <button
          onClick={() => send()}
          disabled={loading || !input.trim()}
          style={{
            background: RED,
            color: OFF_WHITE,
            border: "none",
            borderRadius: 10,
            padding: "0 18px",
            fontFamily: DM,
            fontSize: "0.86rem",
            fontWeight: 500,
            cursor: loading || !input.trim() ? "default" : "pointer",
            opacity: loading || !input.trim() ? 0.5 : 1,
          }}
        >
          Send
        </button>
      </div>

      {/* PDF preview modal — sits above the panel, does NOT collapse the widget */}
      {preview && (
        <div
          role="dialog"
          aria-label="Document preview"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.72)",
          }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) setPreview(null); }}
        >
          <div
            style={{
              background: NEAR_BLACK,
              border: "1px solid rgba(240,237,232,0.14)",
              borderRadius: 12,
              boxShadow: "0 16px 64px rgba(0,0,0,0.6)",
              display: "flex",
              flexDirection: "column",
              width: "min(880px, 92vw)",
              height: "min(700px, 88vh)",
              overflow: "hidden",
            }}
          >
            {/* Modal header */}
            <div
              style={{
                padding: "10px 14px",
                borderBottom: "1px solid rgba(240,237,232,0.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Starburst size={20} />
                <span
                  style={{
                    fontFamily: DM,
                    fontSize: "0.82rem",
                    color: OFF_WHITE,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "60vw",
                  }}
                >
                  {preview.document || "Document"}
                </span>
              </div>
              <button
                onClick={() => setPreview(null)}
                aria-label="Close preview"
                style={{
                  background: "transparent",
                  border: "none",
                  color: MUTED,
                  fontSize: "1.4rem",
                  lineHeight: 1,
                  cursor: "pointer",
                  padding: "0 4px",
                  flexShrink: 0,
                }}
              >
                ×
              </button>
            </div>
            {/* PDF iframe */}
            <iframe
              src={`/api/documents/pdf/${encodeURIComponent(preview.blob_path)}?token=${encodeURIComponent(localStorage.getItem("token") || "")}`}
              title={preview.document || "Document preview"}
              style={{ flex: 1, border: "none", background: "#fff" }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
